import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { ErrorFilledIcon } from '@sharedCR/coreDesignSystem/icons';
import { CompanyManagerPaths } from 'app/configs/routesConfig/companyManager';
import { COMPANY_TABS_ROUTING_PARAMS } from 'app/configs/routesConfig/companyTabs';
import { LoansPaths } from 'app/configs/routesConfig/loans';

import { closePrepareDisclosuresWizardAction } from 'store/loanApplications/actions';

import {
  useDisclosureReadiness,
  usePrepareDisclosuresWizard,
  useRegisterStepGate,
} from '../../../hooks';
import { WizardStep } from '../../../PrepareDisclosuresWizard.constants';
import { ReadinessCheckStatus } from '../../../readiness/readinessChecks.constants';

import BrandingNudge from './components/BrandingNudge';
import LoanSnapshotCard from './components/LoanSnapshotCard';
import ReadinessCheckRow from './components/ReadinessCheckRow';
import {
  cardStyles,
  checklistStyles,
  headerStyles,
  layoutStyles,
  sendAlertIconStyles,
  sendAlertStyles,
  subtitleStyles,
  titleStyles,
} from './ReadinessStep.styles';
import { ReadinessStepTestIds } from './ReadinessStep.testIds';

const SEND_ALERT_MESSAGE =
  'A change made after preview needs attention before you can send. Resolve the highlighted items, then return to Send.';

const getSubtitle = ({ total, passingCount, blockingCount }) =>
  blockingCount > 0
    ? `${passingCount} checks passing · ${blockingCount} need attention before you can continue`
    : `All ${total} checks passing · ready to continue`;

const ReadinessStep = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { applicationId, readinessAlertCheckIds } = usePrepareDisclosuresWizard();
  const { checks, summary, snapshot } = useDisclosureReadiness();
  const [isBrandingDismissed, setIsBrandingDismissed] = useState(false);

  useRegisterStepGate(WizardStep.READINESS, summary.isReadyToContinue);

  const highlightedIds = useMemo(() => {
    const alerted = new Set(readinessAlertCheckIds ?? []);

    return new Set(
      checks
        .filter(({ id, status }) => alerted.has(id) && status !== ReadinessCheckStatus.PASS)
        .map(({ id }) => id),
    );
  }, [readinessAlertCheckIds, checks]);

  const navigateAndClose = useCallback(
    path => {
      dispatch(closePrepareDisclosuresWizardAction());
      history.push(path);
    },
    [dispatch, history],
  );

  const handleFix = useCallback(
    ({ tab }) =>
      navigateAndClose(LoansPaths.applicationManagement.generatePath({ applicationId, tab })),
    [navigateAndClose, applicationId],
  );

  const handleAddLogo = useCallback(
    () =>
      navigateAndClose(
        CompanyManagerPaths.companyManagerTab.generatePath({
          tab: COMPANY_TABS_ROUTING_PARAMS.branding,
        }),
      ),
    [navigateAndClose],
  );

  const handleSkipBranding = useCallback(() => setIsBrandingDismissed(true), []);

  return (
    <Box data-testid={ReadinessStepTestIds.ROOT} sx={layoutStyles}>
      <Box sx={cardStyles}>
        <Box sx={headerStyles}>
          <Typography sx={titleStyles} variant="bold12">
            Disclosure Readiness
          </Typography>
          <Typography
            data-testid={ReadinessStepTestIds.SUBTITLE}
            sx={subtitleStyles}
            variant="regular12"
          >
            {getSubtitle(summary)}
          </Typography>
        </Box>
        {highlightedIds.size > 0 && (
          <Box data-testid={ReadinessStepTestIds.SEND_ALERT} sx={sendAlertStyles}>
            <Box sx={sendAlertIconStyles}>
              <ErrorFilledIcon size={18} />
            </Box>
            <Typography variant="regular12">{SEND_ALERT_MESSAGE}</Typography>
          </Box>
        )}
        <Box data-testid={ReadinessStepTestIds.CHECKLIST} sx={checklistStyles}>
          {checks.map(check => (
            <ReadinessCheckRow
              check={check}
              isHighlighted={highlightedIds.has(check.id)}
              key={check.id}
              onFix={handleFix}
            />
          ))}
        </Box>
        {!isBrandingDismissed && (
          <BrandingNudge onAddLogo={handleAddLogo} onSkip={handleSkipBranding} />
        )}
      </Box>
      <LoanSnapshotCard snapshot={snapshot} summary={summary} />
    </Box>
  );
};

export default memo(ReadinessStep);
